<script setup lang="ts">
import type { Announcement, AnnouncementPrize } from '~~/shared/utils/announcement'

interface Props {
  announcement: Announcement
  /** In preview mode CTA buttons don't navigate. */
  preview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  preview: false
})

const emit = defineEmits<{
  cta: [type: 'primary' | 'secondary']
}>()

const cleanPrizes = computed(() =>
  [...props.announcement.prizes]
    .filter(p => p.label.trim())
    .sort((a, b) => a.place - b.place)
)

interface MedalStyle {
  icon: string
  text: string
  bg: string
  ring: string
  stand: string
  label: string
}

const medal: Record<1 | 2 | 3, MedalStyle> = {
  1: { icon: 'i-lucide-trophy', text: 'text-amber-500', bg: 'bg-amber-500/15', ring: 'ring-amber-500/30', stand: 'h-16', label: '1st' },
  2: { icon: 'i-lucide-medal', text: 'text-slate-400', bg: 'bg-slate-400/15', ring: 'ring-slate-400/30', stand: 'h-11', label: '2nd' },
  3: { icon: 'i-lucide-medal', text: 'text-orange-400', bg: 'bg-orange-500/15', ring: 'ring-orange-500/30', stand: 'h-8', label: '3rd' }
}

interface PodiumEntry {
  place: 1 | 2 | 3
  prize: AnnouncementPrize
  style: MedalStyle
}

// Places 1-3 render as a podium (visual order 2 - 1 - 3). Anything beyond
// third place falls back to a simple list underneath.
const podium = computed<PodiumEntry[]>(() => {
  const byPlace = new Map(cleanPrizes.value.map(p => [p.place, p]))
  return ([2, 1, 3] as const).flatMap((place) => {
    const prize = byPlace.get(place)
    return prize ? [{ place, prize, style: medal[place] }] : []
  })
})

const extraPrizes = computed(() => cleanPrizes.value.filter(p => p.place > 3))
const hasPodium = computed(() => podium.value.length > 0)

const dateLabel = computed(() => {
  const { startDate, endDate } = props.announcement
  const fmt = (iso: string) => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const now = Date.now()

  if (endDate) {
    const end = new Date(endDate)
    if (!Number.isNaN(end.getTime())) {
      const days = Math.ceil((end.getTime() + 24 * 60 * 60 * 1000 - now) / (24 * 60 * 60 * 1000))
      if (days > 1) return `Ends in ${days} days`
      if (days === 1) return 'Ends tomorrow'
      if (days === 0) return 'Ends today'
    }
    return `Ends ${fmt(endDate)}`
  }
  if (startDate) {
    const start = new Date(startDate)
    if (!Number.isNaN(start.getTime()) && start.getTime() > now) return `Starts ${fmt(startDate)}`
  }
  return ''
})
</script>

<template>
  <div class="space-y-5">
    <!-- Header -->
    <div class="flex flex-col items-center text-center gap-3">
      <div class="flex items-center justify-center size-12 rounded-2xl bg-primary-500/12 ring-1 ring-primary-500/20">
        <UIcon
          :name="announcement.icon || 'i-lucide-megaphone'"
          class="size-6 text-primary-400"
        />
      </div>

      <div class="space-y-1.5">
        <UBadge
          v-if="announcement.badge"
          :label="announcement.badge"
          color="primary"
          variant="soft"
          size="sm"
        />
        <h3 class="text-xl font-semibold text-highlighted text-balance">
          {{ announcement.title || 'Announcement title' }}
        </h3>
      </div>

      <p
        v-if="announcement.body"
        class="text-sm text-muted max-w-md whitespace-pre-line"
      >
        {{ announcement.body }}
      </p>

      <UBadge
        v-if="dateLabel"
        :label="dateLabel"
        color="neutral"
        variant="subtle"
        size="sm"
        icon="i-lucide-calendar-clock"
      />
    </div>

    <!-- Prizes podium -->
    <div
      v-if="hasPodium"
      class="rounded-xl border border-default bg-elevated/40 px-4 pt-4 pb-1"
    >
      <div class="flex items-end justify-center gap-2.5 sm:gap-4">
        <div
          v-for="entry in podium"
          :key="entry.place"
          class="flex flex-col items-center gap-2 w-full max-w-32"
        >
          <div
            class="flex items-center justify-center size-11 rounded-full ring-1"
            :class="[entry.style.bg, entry.style.ring]"
          >
            <UIcon
              :name="entry.style.icon"
              class="size-5"
              :class="entry.style.text"
            />
          </div>
          <p class="text-xs font-medium text-highlighted text-center leading-tight text-balance">
            {{ entry.prize.label }}
          </p>
          <div
            class="w-full rounded-t-lg flex items-start justify-center pt-1.5"
            :class="[entry.style.bg, entry.style.stand]"
          >
            <span
              class="text-sm font-bold"
              :class="entry.style.text"
            >
              {{ entry.style.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- Prizes beyond 3rd place -->
      <ul
        v-if="extraPrizes.length"
        class="mt-3 space-y-1.5 border-t border-default pt-3"
      >
        <li
          v-for="prize in extraPrizes"
          :key="prize.place"
          class="flex items-center gap-2 text-sm text-muted"
        >
          <span class="text-xs font-semibold text-dimmed tabular-nums w-6 shrink-0">
            {{ prize.place }}th
          </span>
          {{ prize.label }}
        </li>
      </ul>
    </div>

    <!-- Steps -->
    <div
      v-if="announcement.steps.length"
      class="rounded-xl border border-default bg-elevated/40 p-4"
    >
      <p class="text-xs font-semibold text-dimmed uppercase tracking-wide mb-3">
        How to enter
      </p>
      <ol class="space-y-2.5">
        <li
          v-for="(step, i) in announcement.steps"
          :key="i"
          class="flex items-start gap-3 text-sm text-toned"
        >
          <span class="flex items-center justify-center size-5 rounded-full bg-primary-500/15 text-primary-400 text-xs font-semibold shrink-0 mt-0.5">
            {{ i + 1 }}
          </span>
          <span class="whitespace-pre-line">{{ step }}</span>
        </li>
      </ol>
    </div>

    <!-- CTAs -->
    <div
      v-if="announcement.primaryCta || announcement.secondaryCta"
      class="flex flex-col gap-2"
    >
      <UButton
        v-if="announcement.primaryCta"
        :label="announcement.primaryCta.label"
        :to="preview ? undefined : announcement.primaryCta.url"
        :target="preview ? undefined : '_blank'"
        rel="noopener noreferrer"
        color="primary"
        size="lg"
        block
        trailing-icon="i-lucide-arrow-up-right"
        @click="emit('cta', 'primary')"
      />
      <UButton
        v-if="announcement.secondaryCta"
        :label="announcement.secondaryCta.label"
        :to="preview ? undefined : announcement.secondaryCta.url"
        :target="preview ? undefined : '_blank'"
        rel="noopener noreferrer"
        color="neutral"
        variant="ghost"
        size="lg"
        block
        @click="emit('cta', 'secondary')"
      />
    </div>
  </div>
</template>
